import {
  AgentChat,
  ProposalsHeader,
  resolveAgentVisuals,
  useAgentChildren,
} from '@tunasong/plugin-agent/integration'
import { features, useSelector, useThunkDispatch } from '@tunasong/redux'
import type { Agent, ColorName, Persisted } from '@tunasong/schemas'
import { getColorCSS, Tab, Tabs, Typography, type SidebarComponent } from '@tunasong/ui-lib'
import cn from 'classnames'
import type { FC, SyntheticEvent } from 'react'
import { useState } from 'react'
import { AgentChatEmptyHeader } from './agent-chat-empty-header.js'
import styles from './sidebar-agents.module.css'

const labelFor = (agent: Persisted<Agent>): string =>
  agent.name?.trim() || agent.roleLine?.trim() || 'Agent'

const CHAT_BOX_ID = 'agent-chat-input'

/**
 * Body of the sidebar's `Agents` tool.
 *
 * Lists every agent attached to the host entity (one tab per agent for 2+,
 * single agent for 1) and renders the active agent's chat thread with the
 * `Proposals (n)` strip sticky on top. Active-agent selection is mirrored
 * with the in-header `AgentRail` via `ui.sidebar.activeAgentId` so a click
 * on either surface updates the other.
 *
 * Above the chat feed we render an `AgentChatEmptyHeader` — it self-hides
 * once the active agent has any comments — so a fresh agent panel opens with
 * a role-line cue and clickable starter prompts rather than a blank surface.
 *
 * Empty state covers the cold-cache window where `useAgentChildren` returns
 * `[]` before the children query resolves; the surrounding `disabled`
 * predicate in `sidebar.tsx` filters out entity types that can never host
 * agents, but children-not-yet-loaded leaks through.
 */
export const SidebarAgents: FC<SidebarComponent> = ({ entity, className }) => {
  const dispatch = useThunkDispatch()
  const agents = useAgentChildren(entity?.id)
  const activeAgentId = useSelector(state => state.ui.sidebar.activeAgentId)

  // One-shot draft to inject into the ChatBox when the user clicks a starter
  // prompt. Cleared by the ChatBox's `onDraftConsumed` so a subsequent click
  // on the same prompt still fires (React only re-runs the effect when the
  // string identity changes from null→value).
  const [pendingDraft, setPendingDraft] = useState<string | null>(null)

  if (agents.length === 0) {
    return (
      <Typography variant="caption" className={cn(styles.empty, className)}>
        No agents attached to this entity.
      </Typography>
    )
  }

  // Resolve the active agent: Redux selection wins, otherwise the first
  // attached agent (so a fresh open lands on a real thread instead of
  // empty).
  const activeAgent = agents.find(a => a.id === activeAgentId) ?? agents[0]

  const handleTab = (_event: SyntheticEvent | null, value: string | number) => {
    const next = agents.find(a => a.id === value)
    if (next) {
      dispatch(features.ui.actions.setSidebarActiveAgentId(next.id))
    }
  }

  const handleSelectPrompt = (prompt: string) => {
    // Clear first so even an identical prompt reposts (the effect in ChatBox
    // is keyed on the draft string).
    setPendingDraft(null)
    setPendingDraft(prompt)
  }

  const handleDraftConsumed = () => {
    setPendingDraft(null)
  }

  return (
    <div className={cn(styles.container, className)} role="region" aria-label="Agent chat">
      {agents.length > 1 ? (
        <Tabs value={activeAgent.id} onChange={handleTab} className={styles.tabs}>
          {agents.map(agent => {
            const { color, Icon } = resolveAgentVisuals(agent)
            const iconColor = getColorCSS(color as ColorName)
            return (
              <Tab
                key={agent.id}
                value={agent.id}
                label={labelFor(agent)}
                icon={
                  <Icon
                    className={styles.tabIcon}
                    style={{ color: iconColor }}
                    aria-hidden="true"
                  />
                }
                iconPosition="start"
              />
            )
          })}
        </Tabs>
      ) : null}
      <div className={styles.body}>
        <ProposalsHeader agentId={activeAgent.id} />
        <AgentChatEmptyHeader
          agent={activeAgent}
          chatBoxId={CHAT_BOX_ID}
          onSelectPrompt={handleSelectPrompt}
        />
        <AgentChat
          element={activeAgent}
          showTabs={false}
          pendingDraft={pendingDraft ?? undefined}
          onDraftConsumed={handleDraftConsumed}
          chatBoxId={CHAT_BOX_ID}
        />
      </div>
    </div>
  )
}
